import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { FileText, Plus, Edit2, Copy, Trash2, Eye, EyeOff, FileDown, Printer, Share2 } from 'lucide-react';
import BackButton from '../../components/BackButton';
import { useTemplatesCoopers } from './useTemplatesCoopers';
import TemplatesCoopersModals from './TemplatesCoopersModals';

export default function TemplatesCOOPERSManagement({ userId, getAuthHeader }) {
  const ctx = useTemplatesCoopers(userId, getAuthHeader);
  const { templates, loading, error, success, handleDeleteTemplate, handleDuplicateTemplate, handleLoadDefaultTemplate, toggleTemplateActive, handlePreviewTemplate, handlePrintTemplate, handleDownloadPDF, handleShareTemplate, openEditDialog } = ctx;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        
      {/* Bouton Retour */}
      <BackButton label="Retour" />

<div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des templates...</p>
        </div>
      </div>
    );
  }


  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
            Mes Templates Personnalisés
          </h1>
          <p className="text-gray-600 mt-2">
            Créez et gérez vos propres modèles de documents
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => openEditDialog()} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Template
          </Button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Liste des templates par type */}
      {(() => {
        // Grouper les templates par type dynamiquement
        const groupedTemplates = templates.reduce((acc, template) => {
          const type = template.type_document;
          if (!acc[type]) acc[type] = [];
          acc[type].push(template);
          return acc;
        }, {});

        // Ordre de priorité pour les types standards
        const standardTypes = ['proposition', 'conditions', 'attestation'];
        const allTypes = [...new Set([...standardTypes, ...Object.keys(groupedTemplates)])];

        // Mapper les noms d'affichage
        const typeDisplayNames = {
          'proposition': 'Propositions',
          'conditions': 'Conditions Générales',
          'attestation': 'Attestations'
        };

        return allTypes.map(type => {
          const typeTemplates = groupedTemplates[type] || [];
          if (typeTemplates.length === 0 && !standardTypes.includes(type)) return null;

          const isStandard = standardTypes.includes(type);
          const displayName = typeDisplayNames[type] || type.charAt(0).toUpperCase() + type.slice(1);
          
          return (
            <Card key={type}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  {displayName}
                  <Badge className="bg-blue-600 text-white font-semibold">{typeTemplates.length}</Badge>
                  {!isStandard && <Badge className="bg-purple-700 text-white font-semibold">Personnalisé</Badge>}
                </CardTitle>
                {isStandard && (
                  <CardDescription>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => handleLoadDefaultTemplate(type)}
                      className="p-0 h-auto text-blue-600"
                    >
                      Créer à partir du template par défaut
                    </Button>
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent>
              {typeTemplates.length === 0 ? (
                <p className="text-gray-500 text-center py-4 md:py-8">
                  Aucun template personnalisé pour ce type de document
                </p>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {typeTemplates.map(template => (
                    <Card key={template.id} className={`hover:shadow-lg transition-all ${!template.is_active ? 'opacity-70' : ''}`}>
                      <CardHeader className="pb-2">
                        <div className="flex justify-between items-start">
                          <CardTitle className="text-lg">{template.nom}</CardTitle>
                          {template.is_active ? (
                            <Badge className="bg-green-600 text-white font-semibold">Actif</Badge>
                          ) : (
                            <Badge className="bg-gray-400 text-white font-semibold">Inactif</Badge>
                          )}
                        </div>
                        {template.description && (
                          <CardDescription className="text-sm line-clamp-2">
                            {template.description}
                          </CardDescription>
                        )}
                      </CardHeader>
                      <CardContent className="pt-2">
                        {/* Actions principales */}
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8"
                            onClick={() => handlePreviewTemplate(template)}
                            title="Voir le template"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Voir
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8"
                            onClick={() => openEditDialog(template)}
                            title="Modifier"
                          >
                            <Edit2 className="w-3 h-3 mr-1" />
                            Éditer
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8"
                            onClick={() => handleDuplicateTemplate(template.id)}
                            title="Dupliquer"
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                        
                        {/* Actions secondaires */}
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            onClick={() => handlePrintTemplate(template)}
                            title="Imprimer"
                          >
                            <Printer className="w-3 h-3 mr-1" />
                            Imprimer
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                            onClick={() => handleDownloadPDF(template)}
                            title="Télécharger PDF"
                          >
                            <FileDown className="w-3 h-3 mr-1" />
                            PDF
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => handleShareTemplate(template)}
                            title="Partager"
                          >
                            <Share2 className="w-3 h-3 mr-1" />
                            Partager
                          </Button>
                        </div>
                        
                        {/* Actions de gestion */}
                        <div className="flex flex-wrap gap-1.5 pt-2 border-t">
                          <Button
                            size="sm"
                            variant={template.is_active ? "outline" : "default"}
                            className={`h-8 ${template.is_active ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50' : 'bg-green-600 hover:bg-green-700'}`}
                            onClick={() => toggleTemplateActive(template)}
                          >
                            {template.is_active ? (
                              <><EyeOff className="w-3 h-3 mr-1" /> Désactiver</>
                            ) : (
                              <><Eye className="w-3 h-3 mr-1" /> Activer</>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="h-8"
                            onClick={() => handleDeleteTemplate(template.id)}
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Supprimer
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          );
        });
      })()}

      <TemplatesCoopersModals ctx={ctx} />
    </div>
  );
}
